import { useQuery } from '@tanstack/react-query';
import { fetchProfitDashboard } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { MetricCard } from '../components/ui/MetricCard';

// Owner-only company financials — unmodified appGetProfitDashboard(). Gated
// server-side on canViewFinancials; this page assumes the nav already
// hides it from everyone else and simply surfaces whatever the server
// allows.
export function ProfitDashboardPage() {
  const query = useQuery({ queryKey: ['profitDashboard'], queryFn: fetchProfitDashboard });

  if (query.isLoading) return <LoadingState label="Loading profit dashboard…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the profit dashboard.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load the profit dashboard.'} onRetry={() => query.refetch()} />;
  }

  const { currentWeek, ytd, recentWeeks = [], recentPaidJobs = [] } = query.data;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Owner" title="Profit Dashboard" />

      {currentWeek && (
        <section className="space-y-2">
          <h2 className="text-sm font-bold text-white">
            Current Week ({currentWeek.weekStart} – {currentWeek.weekEnd})
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricCard label="Gross" value={currentWeek.grossText} money />
            <MetricCard label="Net" value={currentWeek.netText} money />
            <MetricCard label="Company Share" value={currentWeek.companyShareText} money />
            <MetricCard label="Job Expenses" value={currentWeek.jobExpensesText} money />
          </div>
        </section>
      )}

      {ytd && (
        <section className="space-y-2">
          <h2 className="text-sm font-bold text-white">Year to Date</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricCard label="Gross" value={ytd.grossText} money />
            <MetricCard label="Net" value={ytd.netText} money />
            <MetricCard label="Company Share" value={ytd.companyShareText} money />
            <MetricCard label="Job Expenses" value={ytd.jobExpensesText} money />
          </div>
        </section>
      )}

      {recentWeeks.length > 0 && (
        <Card>
          <CardHeader title="Recent Weeks" />
          <ul className="space-y-2">
            {recentWeeks.map((w, i) => (
              <li key={i} className="flex items-center justify-between gap-3 rounded-lg border border-ink-border bg-ink p-3 text-sm">
                <span className="text-slate-300">
                  {w.weekStart} – {w.weekEnd}
                </span>
                <span className="font-semibold text-gold-200">{w.netText}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {recentPaidJobs.length > 0 && (
        <Card>
          <CardHeader title="Recently Paid Jobs" />
          <ul className="space-y-2">
            {recentPaidJobs.map((j) => (
              <li key={j.jobId} className="flex items-center justify-between gap-3 rounded-lg border border-ink-border bg-ink p-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{j.customerJob}</p>
                  <p className="text-xs text-slate-500">{j.completionDate}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-semibold text-gold-200">{j.revenueText}</p>
                  <p className="text-xs text-slate-500">margin {j.marginText}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
